import { useEffect, useState, useContext } from "react";
import { message } from "antd";
import { UploadFile } from "antd/es/upload/interface";
import { AppContext } from "src/context";

import BackgroundUploadQueue from "src/common/base/BackgroundUploadQueue";
import IndexedDBManager from "src/common/utils/IndexedDBManager";

import {
  DB_NAME,
  DB_VERSION,
  FILE_STORE_NAME,
  FILE_STORE_CONFIG,
} from "src/config/db/config";

const BackgroundUploader = () => {
  const { state, dispatch } = useContext(AppContext);
  const [dbManager, setDbManager] = useState<IndexedDBManager | null>(null);

  const initDB = async () => {
    try {
      const manager = new IndexedDBManager(DB_NAME, DB_VERSION, [
        FILE_STORE_CONFIG,
      ]);
      await manager.init();
      setDbManager(manager);
    } catch (error) {
      console.warn("Error initializing DB, resetting...");
      await resetStore();
    }
  };

  const resetStore = async () => {
    await initDB();
    await dbManager?.clearStore(FILE_STORE_NAME);
  };

  const saveFileToDB = async (
    file: UploadFile,
    type: string,
    value: string,
    delete_previous?: boolean,
    callback?: (data: { source: string; hash: string }) => void | undefined
  ) => {
    if (!dbManager || !file.originFileObj) return;

    try {
      await dbManager.put(FILE_STORE_NAME, {
        uid: file.uid,
        file: file.originFileObj as File,
        retries: 0,
        meta: {
          type,
          value,
          delete_previous: delete_previous || false,
          callback: callback || undefined,
        },
        type_value: [type, value],
      });
    } catch (error) {
      message.error("Error saving file. Resetting database...");
      await resetStore();
      await saveFileToDB(file, type, value);
    }
  };

  useEffect(() => {
    initDB();
  }, []);

  useEffect(() => {
    const uploadFiles = async () => {
      const files = state.data.files;

      if (Array.isArray(files) && files.length > 0) {
        for (const file of files) {
          await saveFileToDB(
            file,
            state.data.type,
            state.data.value,
            state.data.delete_previous
          );
        }

        dispatch({
          type: "TRIGGER_UPLOAD",
          data: {
            reinit_upload: !state.reinit_upload,
            callback: state.data.callback,
          },
        });
      }
    };

    uploadFiles();
  }, [state.data]);

  return <BackgroundUploadQueue />;
};

export default BackgroundUploader;
